export type BrokerProvider = "openai-codex" | "anthropic";
export type BrokerWindow = { name: string; used: number; resetAt?: number };
export type BrokerAccount = {
  id: string;
  state: "ready" | "expired" | "missing";
  hasAccess: boolean;
  windows: BrokerWindow[];
  meteredAt: number | null;
};
export type BrokerConnection = {
  id: string;
  provider: BrokerProvider;
  instance: string;
  accounts: BrokerAccount[];
  syncedAt: string | null;
  available: boolean;
  cooldowns: Record<string, number>;
};
export type BrokerSelection = { connection: string; account: string | null };
export type AccessLease = { access: string; expires: number; accountId?: string };

export function brokerProvider(value: unknown): value is BrokerProvider {
  return value === "openai-codex" || value === "anthropic";
}

export function brokerAccountId(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length <= 254 &&
    /^[A-Za-z0-9][A-Za-z0-9._%+@-]*$/.test(value) &&
    !["codex", "openai-codex", "anthropic", "venice"].includes(value)
  );
}

export function parseAccessLease(value: unknown, now = Date.now()): AccessLease {
  const row = value as AccessLease | null;
  if (
    !row ||
    typeof row !== "object" ||
    Array.isArray(row) ||
    Object.keys(row).some((key) => !["access", "expires", "accountId"].includes(key)) ||
    typeof row.access !== "string" ||
    !row.access ||
    row.access.length > 65536 ||
    /[^!-~]/.test(row.access) ||
    !Number.isSafeInteger(row.expires) ||
    row.expires <= now ||
    (row.accountId !== undefined &&
      (typeof row.accountId !== "string" || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/.test(row.accountId)))
  )
    throw new Error("broker access lease is invalid or expired");
  return { access: row.access, expires: row.expires, ...(row.accountId ? { accountId: row.accountId } : {}) };
}

export function brokerAvailability(
  connection: BrokerConnection,
  account: BrokerAccount,
  model: string,
  now = Date.now(),
): "available" | "unavailable" | "exhausted" | "cooldown" {
  if (!connection.available || !account.hasAccess || account.state === "missing") return "unavailable";
  if ((connection.cooldowns[account.id] ?? 0) > now) return "cooldown";
  if (
    account.windows.some(
      (window) =>
        (window.name !== "7d OI" || model.includes("fable")) &&
        window.used >= 100 &&
        (window.resetAt === undefined || window.resetAt > now),
    )
  )
    return "exhausted";
  return "available";
}

export function chooseBrokerAccount(
  connection: BrokerConnection,
  pinned: string | null,
  model: string,
  now = Date.now(),
): BrokerAccount {
  const accounts =
    pinned === null ? connection.accounts : connection.accounts.filter((one) => one.id === pinned);
  const chosen = accounts.find((one) => brokerAvailability(connection, one, model, now) === "available");
  if (chosen) return chosen;
  const resets = accounts
    .flatMap((one) => [connection.cooldowns[one.id], ...one.windows.map((window) => window.resetAt)])
    .filter((one): one is number => typeof one === "number" && one > now);
  const next = resets.length
    ? ` Next known reset: ${new Date(Math.min(...resets)).toISOString()}.`
    : " No reset is known; ask the operator to sync broker metadata.";
  throw new Error(
    `${pinned === null ? "No eligible broker account" : "The pinned broker account is unavailable; it will not rotate"} for ${connection.provider}.${next}`,
  );
}
